import React, { useState, useEffect, useMemo, useCallback, memo, Suspense, useRef, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useGlobalPoints } from '../../contexts/GlobalPointsContext';
import { useWellness } from '../../hooks/useWellness';
import WellnessCharacter from '../../component/Wellness/WellnessCharacter';
import WellnessEggCounter from '../../component/Wellness/WellnessEggCounter';
import FriendList from '../../component/Wellness/FriendList';
import FriendInfoBottomSheet from '../../component/Wellness/FriendInfoBottomSheet';
// import SleepAnalysisCard from '../../component/Wellness/SleepAnalysisCard';
import { getBackgroundStyle } from './WellnessDesign';
import '../Wellness.css';
import FriendAddPopup from '../../component/Wellness/FriendAddPopup';
import WellnessMoneyIcon from '../../components/Wellness/WellnessMoneyIcon';
import HuvleNewsButton from '../../components/Wellness/HuvleNewsButton';
import { useWellnessEarning } from '../../hooks/useWellnessEarning';
// 🚀 조건부 컴포넌트 lazy loading
const FriendInviteTutorialPopup = lazy(() => import('../../component/V4/FriendInviteTutorialPopup'));
// import WellnessBannerAd from '../../components/Wellness/WellnessBannerAd'; // 임시 주석처리

// 수면분석 관련 상수 (임시 비활성화)
// const CONSTANTS = {
//     SKELETON_ITEMS_COUNT: 3,
//     PULSE_ANIMATION_DURATION: '1.5s',
//     WEEKLY_DATA_SIZE: 28,
//     DAYS_IN_WEEK: 7,
//     DEFAULT_REST_SCORE: '??',
//     DEFAULT_DAILY_AVERAGE: '0분',
//     ANIMATION_DELAYS: {
//         CRACK_FADE: 500,
//         SKELETON_PULSE: 1500,
//         COMPONENT_LOAD: 100
//     }
// };

// 포인트 동기화 쿨다운 시간 (중복 방지)
const SYNC_COOLDOWN = 2000; // 2초

const WellnessScreen = memo(() => {
    // 🚀 SPA 최적화: UserContext에서 즉시 데이터 가져오기 (StudyCafeV4와 동일)
    const { tokenUserID: contextTokenUserID, userData: contextUserData, loading: contextLoading } = useUser();
    const tokenUserID = contextTokenUserID;
    const userDataFromParent = contextUserData;
    const userDataLoading = contextLoading;
    const hasTokenInStorage = Boolean(localStorage.getItem('token'));
    const shouldRedirectToLogin = !userDataLoading && !tokenUserID && !hasTokenInStorage;
    
    const navigate = useNavigate();
    const location = useLocation(); // 페이지 전환 감지용
    
    // 인증 상태가 확정된 뒤 토큰이 없으면 로그인으로 복귀
    useEffect(() => {
        if (shouldRedirectToLogin) {
            localStorage.setItem('from', 'Wellness');
            navigate('/V4/Start', { replace: true });
        }
    }, [navigate, shouldRedirectToLogin]);
    
    // goto 파라미터 처리 (RN EggExchange에서 daily-earning으로 이동)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const goto = params.get('goto');
        if (goto === 'daily-earning') {
            navigate('/Wellness/DailyEarning', { replace: true });
        }
    }, [location.search, navigate]);

    // DailyEarning에서 돌아왔는지 확인하는 상태
    const [isReturningFromDailyEarning, setIsReturningFromDailyEarning] = useState(false);
    // 선택된 친구 정보 (모달 표시용)
    const [selectedFriend, setSelectedFriend] = useState(null);
    
    // 전역 포인트 상태 사용 (서버 우선 동기화)
    const { forceSync } = useGlobalPoints();
    const forceSyncRef = useRef(forceSync); // 최신 함수 참조 유지
    
    // forceSync 함수가 변경될 때마다 ref 업데이트
    useEffect(() => {
        forceSyncRef.current = forceSync;
    }, [forceSync]);
    
    // 마지막 동기화 시간 추적 (중복 방지)
    const lastSyncTimeRef = useRef(0);
    const {
        friendStatus,
        realFriends,
        friendCount,
        hasFriends,
        hasAlarm,
        isStudying,
        isFriendDataLoaded,
        showFriendInviteTutorial,
        setShowFriendInviteTutorial,
        onAddFriend,
        userData,
        checkAlarmStatus,
        handlePlusClick,
        showPopup,
        setShowPopup,
        receiverNickname,
        setReceiverNickname,
        onReceiverNicknameChange,
        onDeleteFriend,
    } = useWellness(tokenUserID, userDataFromParent, isReturningFromDailyEarning);

    // 웰니스 돈 수집 기능 - 초기 렌더링 후 지연 로드 (마운트 시간 최적화)
    const [shouldLoadEarning, setShouldLoadEarning] = useState(false);
    
    useEffect(() => {
        // 초기 렌더링 완료 후 다음 프레임에서 로드
        requestAnimationFrame(() => {
            setShouldLoadEarning(true);
        });
    }, []);
    
    // 조건부로 훅 호출 (React 규칙 위반 방지를 위해 항상 호출하되, 내부에서 지연 처리)
    const wellnessEarning = useWellnessEarning(shouldLoadEarning ? tokenUserID : null);

    // const [showEarningPopup, setShowEarningPopup] = useState(false); // 주석처리 (DailyEarning 페이지로 직접 이동)
    // 수면분석 관련 상태 임시 비활성화
    // const [weeklySleepData, setWeeklySleepData] = useState(Array(CONSTANTS.WEEKLY_DATA_SIZE).fill(false));
    // const [isSleepDataLoading, setIsSleepDataLoading] = useState(false);
    // const [dailyAverage, setDailyAverage] = useState(CONSTANTS.DEFAULT_DAILY_AVERAGE);
    
    // 수면분석 데이터 임시 비활성화
    // const sleepAnalysisData = useMemo(() => ({
    //     dailyAverage,
    //     restScore: CONSTANTS.DEFAULT_REST_SCORE
    // }), [dailyAverage]);

    // 시간 문자열을 분으로 변환하는 함수 (HH:MM:SS → 분) - 임시 비활성화
    // const convertTimeToMinutes = useCallback((timeString) => {
    //     if (!timeString) return 0;
    //     
    //     const parts = timeString.split(':');
    //     if (parts.length === 3) {
    //         const hours = parseInt(parts[0], 10);
    //         const minutes = parseInt(parts[1], 10);
    //         const seconds = parseInt(parts[2], 10);
    //         return hours * 60 + minutes + (seconds / 60);
    //     }
    //     return 0;
    // }, []);

    // 수면분석 관련 함수 임시 비활성화
    // const fetchDailyRestTime = useCallback(async () => {
    //     if (!tokenUserID) return;

    //     try {
    //         const studyResponse = await fetch(`${getApiUrl()}/v2/studytimer/totalStudyTime7?user_id=${tokenUserID}`);
    //         const studyDataJson = await studyResponse.json();
            
    //         if (studyDataJson && studyDataJson.total_rest_time) {
    //             const totalRestTimeMinutes = convertTimeToMinutes(studyDataJson.total_rest_time);
    //             const dailyAverageMinutes = Math.round((totalRestTimeMinutes / CONSTANTS.DAYS_IN_WEEK) * 10) / 10;
                
    //             setDailyAverage(`${dailyAverageMinutes}분`);
    //         } else {
    //             setDailyAverage(CONSTANTS.DEFAULT_DAILY_AVERAGE);
    //         }
    //     } catch (error) {
    //         setDailyAverage(CONSTANTS.DEFAULT_DAILY_AVERAGE);
    //     }
    // }, [tokenUserID, convertTimeToMinutes]);

    // const fetchWeeklySleepData = useCallback(async () => {
    //     if (!tokenUserID) return;
    //     // setIsSleepDataLoading(true);
    //     try {
    //         const sleepResponse = await fetch(`${getApiUrl()}/v2/studytimer/sleepPattern7Days?user_id=${tokenUserID}`);
    //         const sleepPatternDataJson = await sleepResponse.json();
    //         if (sleepPatternDataJson && sleepPatternDataJson.timeSlots && sleepPatternDataJson.sleepData) {
    //             const weeklyData = [];
    //             const timeGroups = [
    //                 ['00:00', '03:00'],
    //                 ['06:00', '09:00'],
    //                 ['12:00', '15:00'],
    //                 ['18:00', '21:00']
    //             ];
    //             const days = ['일', '월', '화', '수', '목', '금', '토'];
    //             for (let i = 6; i >= 0; i--) {
    //                 const date = new Date();
    //                 date.setDate(date.getDate() - i);
    //                 const dayName = days[date.getDay()];
    //                 const dayData = sleepPatternDataJson.sleepData.find(d => d.day === dayName);
    //                 if (dayData && dayData.sleepTimes) {
    //                     timeGroups.forEach(timeGroup => {
    //                         const hasRecord = timeGroup.some(time => dayData.sleepTimes[time]);
    //                         weeklyData.push(hasRecord);
    //                     });
    //                 } else {
    //                     for (let j = 0; j < 4; j++) {
    //                         weeklyData.push(false);
    //                     }
    //                 }
    //             }
    //             while (weeklyData.length < CONSTANTS.WEEKLY_DATA_SIZE) {
    //                 weeklyData.push(false);
    //             }
    //             setWeeklySleepData(weeklyData.slice(0, CONSTANTS.WEEKLY_DATA_SIZE));
    //         } else {
    //             setWeeklySleepData(Array(CONSTANTS.WEEKLY_DATA_SIZE).fill(false));
    //         }
    //     } catch (error) {
    //         setWeeklySleepData(Array(CONSTANTS.WEEKLY_DATA_SIZE).fill(false));
    //     } finally {
    //         setIsSleepDataLoading(false);
    //     }
    // }, []);

    // const fetchDailyRestTime = useCallback(async () => {
    //     // 수면분석 관련 함수 임시 비활성화
    //     return;
    // }, []);


    // Wellness 래퍼의 로직 통합: from 정보 저장 및 DailyEarning에서 돌아왔는지 확인
    useEffect(() => {
        // from 정보 저장 (Wellness 래퍼 로직)
        localStorage.setItem('from', "Wellness");
        
        // DailyEarning에서 돌아왔는지 확인
        const fromPath = localStorage.getItem('from');
        if (fromPath === 'Wellness/DailyEarning') {
            setIsReturningFromDailyEarning(true);
            localStorage.removeItem('from');
        }
    }, []);

    // 포인트 동기화 함수 (재사용 가능 + 중복 방지)
    const syncPoints = useCallback(async (force = false) => {
        if (!tokenUserID) return;
        
        // 🚨 중복 방지: 쿨다운 시간 확인
        const now = Date.now();
        if (!force && (now - lastSyncTimeRef.current) < SYNC_COOLDOWN) {
            return;
        }
        
        lastSyncTimeRef.current = now;
        
        try {
            // 1. 큐 플러시 (ScholarshipCard에서 온 포인트 처리)
            const pointsManager = (await import('../../services/pointsManager')).default;
            
            // 큐 상태 확인
            const queueStatus = pointsManager.getQueueStatus();
            if (queueStatus.queueLength > 0 || queueStatus.totalPendingPoints > 0) {
                await pointsManager.forceFlush();
                // 🚀 최적화: 0.5초 대기 제거 - 서버 응답을 기다리지 않고 즉시 동기화
                // 서버가 아직 처리 중이면 다음 폴링에서 자동으로 동기화됨
            }
            
            // 3. 강제 동기화 (서버에서 최신 포인트 가져오기)
            await forceSyncRef.current();
        } catch (error) {
            console.error('❌ [WellnessScreen] 포인트 동기화 실패:', error);
            // 에러가 나도 동기화 시도
            try {
                await forceSyncRef.current();
            } catch (retryError) {
                console.error('❌ [WellnessScreen] 포인트 동기화 재시도 실패:', retryError);
            }
        }
    }, [tokenUserID, location.pathname]);

    // 포인트 동기화: tokenUserID 준비 시 1회 실행 (ref 사용으로 함수 reference 변경에 의한 재실행 방지)
    useEffect(() => {
        if (!tokenUserID) return;
        forceSyncRef.current();
    }, [tokenUserID]);

    // 🚨 페이지 포커스/가시성 변경 시에도 동기화 (페이지를 왔다갔다 할 때)
    useEffect(() => {
        if (!tokenUserID) return;

        const handleVisibilityChange = () => {
            if (!document.hidden && location.pathname === '/Wellness') {
                // 페이지가 다시 보일 때만 동기화
                syncPoints();
            }
        };

        const handleFocus = () => {
            if (location.pathname === '/Wellness') {
                syncPoints();
            }
        };

        // Visibility API 이벤트 리스너
        document.addEventListener('visibilitychange', handleVisibilityChange);
        // Window focus 이벤트 리스너
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [tokenUserID, location.pathname, syncPoints]);

    // 수면분석 관련 useEffect 임시 비활성화
    // useEffect(() => {
    //     fetchDailyRestTime();
    //     fetchWeeklySleepData();
    // }, [fetchDailyRestTime, fetchWeeklySleepData]);

    // LCP 최적화: 배경 스타일을 간소화하여 렌더링 속도 향상
    // 토스 가이드라인: 응집성 - 관련 로직 그룹화
    const memoizedBackgroundStyle = useMemo(() => 
        getBackgroundStyle(hasAlarm, hasFriends, isStudying, false)
    , [hasAlarm, hasFriends, isStudying]);

    // 최적화된 이벤트 핸들러들
    const handlePopupClose = useCallback(() => {
        setShowPopup(false);
        setReceiverNickname('');
    }, [setShowPopup, setReceiverNickname]);

    const handleAddFriendSubmit = useCallback(() => {
        if (!receiverNickname.trim()) {
            alert('친구 코드를 입력해주세요.');
            return;
        }
        
        if (receiverNickname.trim() === userData?.user_nickname) {
            alert('본인의 친구 코드는 추가할 수 없어요.');
            return;
        }
        
        onAddFriend(receiverNickname);
        setReceiverNickname('');
        setShowPopup(false);
    }, [receiverNickname, onAddFriend, userData?.user_nickname, setShowPopup, setReceiverNickname]);

    // 알람 버튼 클릭 핸들러
    const handleAlarmClick = useCallback(async () => {
        await checkAlarmStatus();
    }, [checkAlarmStatus]);
    
    // 캐릭터 클릭 핸들러 - Mission 페이지로 이동
    const handleCharacterClick = useCallback(() => {
        navigate('/Wellness/Mission');
    }, [navigate]);

    // 돈 아이콘 클릭 핸들러 - DailyEarning 페이지로 직접 이동
    const handleMoneyIconClick = useCallback(() => {
        navigate('/Wellness/DailyEarning');
    }, [navigate]);

    // 돈 수집 팝업 닫기 핸들러 - 주석처리 (DailyEarning 페이지로 직접 이동)
    // const handleEarningPopupClose = useCallback(() => {
    //     setShowEarningPopup(false);
    // }, []);


    // 빨간 점 애니메이션 제거
    
    // useWellness 훅에서 이미 친구 초대 튜토리얼 팝업을 관리하므로 중복 제거
    // useEffect(() => {
    //     if (tokenUserID && isFriendDataLoaded) {
    //         setShowFriendInviteTutorial(true);
    //     }
    // }, [tokenUserID, isFriendDataLoaded, setShowFriendInviteTutorial]);

    // LCP 최적화: Early Return 제거하여 즉시 UI 렌더링
    // 토스 가이드라인: 예측 가능성 - 로딩 상태와 관계없이 UI 먼저 표시

    // 🚀 최적화: loading 상태 체크 추가 - UserContext 초기화 중 깜빡임 방지
    if (userDataLoading) {
        return <div style={{ minHeight: '100vh', backgroundColor: '#FFF9E6' }} />;
    }

    // 토큰이 없을 때도 부정적 메시지 대신 중립 로딩 화면 유지
    // - localStorage에 토큰 있음: UserContext 처리 중(race) 또는 토큰 검증 중
    // - localStorage에 토큰 없음: shouldRedirectToLogin effect가 로그인으로 복귀 처리
    if (!tokenUserID) {
        return <div style={{ minHeight: '100vh', backgroundColor: '#FFF9E6' }} />;
    }

    // 메인 렌더링 로직
    // 🚀 LCP 최적화: return null 제거 - 즉시 화면 표시
    return (
        <div
            className="wellness-container"
            style={{
                ...styles.mainContainer,
                background: memoizedBackgroundStyle
            }}
        >
            {/* 오른쪽 상단 에그 카운터 - 클릭 가능하도록 수정 */}
            <div 
                style={{
                    ...styles.eggCounterContainer,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    navigate('/Wellness/Exchange');
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                }}
                title="에그 교환소로 이동"
            >
                <WellnessEggCounter backgroundColor={memoizedBackgroundStyle} />
            </div>
            


            {/* 돈 모양 아이콘 - 에그 카운터 아래 (모든 사용자에게 표시) */}
            <WellnessMoneyIcon
                onClick={handleMoneyIconClick}
                canClick={wellnessEarning.canClick}
                isClicking={wellnessEarning.isClicking}
                dailyEarned={wellnessEarning.dailyEarned}
                maxDailyEarning={wellnessEarning.maxDailyEarning}
                progressPercentage={wellnessEarning.progressPercentage}
            />

            {/* Huvle 뉴스 버튼 - 안드로이드 + 앱 버전 1.4.9 이상일 때만 표시 */}
            <HuvleNewsButton />

            {/* 친구 리스트 컨테이너 - 직접 렌더링 */}
            <div style={styles.friendListContainer}>
                <FriendList
                    realFriends={realFriends}
                    isButtonMoving={false}
                    onPlusClick={handlePlusClick}
                    onFriendClick={setSelectedFriend}
                    isFriendDataLoaded={isFriendDataLoaded}
                    shouldBlink={false}
                    showRedDot={false}
                    isRedDotAppearing={false}
                />
            </div>

            {selectedFriend && (
                <FriendInfoBottomSheet
                    friend={selectedFriend}
                    onClose={() => setSelectedFriend(null)}
                    onDelete={onDeleteFriend}
                />
            )}

            {/* 팝업과 캐릭터 - 직접 렌더링 */}
            <FriendAddPopup
                showPopup={showPopup}
                userData={userData}
                receiverNickname={receiverNickname}
                onReceiverNicknameChange={(e) => onReceiverNicknameChange(e.target.value)}
                onClose={handlePopupClose}
                onAddFriend={handleAddFriendSubmit}
            />
            
            {/* 캐릭터 컨테이너 - 독립적으로 위치 */}
            {/* LCP 최적화: 즉시 렌더링 (loading="eager", fetchpriority="high") */}
            <div 
                style={{...styles.characterContainer, cursor: 'pointer'}} 
                className="character-container"
                onClick={handleCharacterClick}
            >
                <WellnessCharacter
                    hasAlarm={hasAlarm}
                    hasFriends={hasFriends}
                    friendCount={friendCount}
                    friendStatus={friendStatus}
                    tokenUserID={tokenUserID}
                    isStudying={isStudying}
                    isRest={false}
                    dangerouslySetInnerHTML={true}
                    className={hasFriends ? 'friends-active' : ''}
                    onAlarmClick={handleAlarmClick}
                />
            </div>

            {/* 크랙 애니메이션 제거 */}

            {/* 돈 수집 팝업 (모든 사용자에게 표시) - 주석처리 (DailyEarning 페이지로 직접 이동) */}
            {/* {showEarningPopup && (
                <Suspense fallback={<div></div>}>
                    <WellnessEarningPopup
                        isVisible={showEarningPopup}
                        onClose={handleEarningPopupClose}
                        onCollectMoney={wellnessEarning.collectEggs}
                        canClick={wellnessEarning.canClick}
                        isClicking={wellnessEarning.isClicking}
                        dailyEarned={wellnessEarning.dailyEarned}
                        maxDailyEarning={wellnessEarning.maxDailyEarning}
                        remainingEggs={wellnessEarning.remainingEggs}
                        progressPercentage={wellnessEarning.progressPercentage}
                        clickEffects={wellnessEarning.clickEffects}
                        isMaxEarned={wellnessEarning.isMaxEarned}
                        userData={userData}
                    />
                </Suspense>
            )} */}

            {/* 친구 초대 튜토리얼 팝업 - 큰 컴포넌트만 Suspense 유지 */}
            {/* 🚨 친구 데이터 로딩 완료 후에만 렌더링 (모달 깜빡임 방지) */}
            {isFriendDataLoaded && (
                <Suspense fallback={<div></div>}>
                    <FriendInviteTutorialPopup
                        isVisible={showFriendInviteTutorial}
                        onClose={() => {
                            // FriendInviteTutorialPopup 내부의 "2주간 보지 않기" 로직 사용
                            setShowFriendInviteTutorial(false);
                        }}
                        userData={userData}
                    />
                </Suspense>
            )}

            {/* 수면분석 카드 임시 비활성화
            {!isSleepDataLoading && (
                <div style={styles.sleepAnalysisContainer} className="sleep-analysis-container">
                    <SleepAnalysisCard
                        dailyAverage={sleepAnalysisData.dailyAverage}
                        restScore={sleepAnalysisData.restScore}
                        weeklyData={weeklySleepData}
                        isLoading={false}
                        clockIconSize={{ width: '13px', height: '13px', flexShrink: 0 }}
                        onClick={() => navigate('/Wellness/SleepAnalysis')}
                    />
                </div>
            )}
            */}
        </div>
    );
});

const styles = {
    mainContainer: {
        // 기본 스타일만 정의, background는 동적으로 적용
    },
    errorContainer: {
        width: '100%',
        height: '100vh',
        backgroundColor: '#FFF9E6',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    errorContent: {
        textAlign: 'center'
    },
    errorTitle: {
        marginBottom: '20px',
        fontSize: '18px',
        color: '#666'
    },
    errorMessage: {
        fontSize: '14px',
        color: '#999'
    },
    eggCounterContainer: {
        position: 'fixed',
        top: '20px', /* 30px → 20px로 10px 위로 이동 */
        right: '20px',
        zIndex: 10000,
        pointerEvents: 'auto'
    },
    friendListContainer: {
        position: 'fixed',
        top: '-20px',
        left: '0px',
        right: '0px',
        height: '140px',
        overflow: 'hidden',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        touchAction: 'pan-y',
        boxSizing: 'border-box',
        pointerEvents: 'none',
    },
    scrollbarStyle: `
        /* Chrome, Safari, Opera - 스크롤바 완전 제거 */
        div::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
        }
        
        div::-webkit-scrollbar-track {
            display: none !important;
        }
        
        div::-webkit-scrollbar-thumb {
            display: none !important;
        }
        
        /* 스크롤 방지 강화 */
        div {
            overscroll-behavior-x: none !important;
            overscroll-behavior-y: auto !important;
            touch-action: pan-y !important;
        }
    `,
    sleepAnalysisContainer: {
        position: 'relative',
        marginTop: '50px',
        marginBottom: '60px',
        width: '100%',
        maxWidth: '400px',
        padding: '0 20px',
        boxSizing: 'border-box',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    bannerAdStyle: {
        marginTop: '0px',
        marginBottom: '0px',
        width: '100%'
    },
    characterContainer: {
        position: 'relative',
        marginTop: '190px', /* 240px → 190px로 50px 위로 이동 */
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }
};

export default WellnessScreen;